/**
 * Settings — everything a person can change about this copy of the platform,
 * on one screen.
 *
 * Until now the settings were wherever the thing they governed happened to
 * live: the theme in the header, the menu at the foot of the sidebar, Sky's
 * behind the tabs of her own panel, and the connection nowhere at all — it
 * was baked into the build. An operator who could not find where to allow
 * the microphone was right that there was nowhere to find it.
 *
 * The screen changes the real things, through the same hooks the rest of the
 * shell uses, so nothing here is a second copy that can drift. Where a
 * setting is per browser rather than per person, the section says so.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import { useAssistant } from "../../assistant/AssistantContext";
import { Access, Learning, Look, Sources, VoiceSettings } from "../../assistant/SettingsPanels";
import * as ears from "../../assistant/listen";
import * as voice from "../../assistant/voice";
import {
  builtConfig,
  clearConnectionOverride,
  config,
  loadConnectionOverride,
  saveConnectionOverride,
  type ConnectionOverride,
} from "../../config/env";
import { Button, Card, Field, Input, Segmented, Select } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { CustomizeMenuDialog } from "../../layout/CustomizeMenuDialog";
import { useNavLayout } from "../../layout/NavLayoutContext";
import { useTheme, type Theme } from "../../layout/theme";
import { t, useI18n, type Lang } from "../../i18n";
import s from "./settings.module.css";

export function SettingsPage() {
  const session = useSession();
  const navigate = useNavigate();

  /*
   * Every setting here takes effect as it is changed — there is no separate
   * save, and the header says so. What the screen needs is a way out: Done
   * goes back to wherever the person came from, or to the dashboard when
   * Settings was the first screen opened; Escape does the same.
   */
  const leave = useCallback(() => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  }, [navigate]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      /* not while a dialog or a text field has the key */
      if (document.querySelector("dialog[open]")) return;
      const tag = (document.activeElement as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      leave();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [leave]);

  return (
    <Page
      eyebrow="Platform"
      title="Settings"
      actions={
        <>
          <span className={s.note}>{t("Changes are saved as you make them.")}</span>
          <Button variant="ghost" onClick={leave} aria-label={t("Back")}>
            {t("Back")}
          </Button>
          <Button variant="primary" onClick={leave}>
            {t("Done")}
          </Button>
        </>
      }
    >
      <div className={s.grid}>
        <Appearance />
        <Menu />
        <Microphone />
        <Card title="Sky · her look and her greeting" sub="Just for you, saved in this browser">
          <div className={s.embedded}>
            <Look />
          </div>
        </Card>
        <Card title="Sky · voice and listening" sub="hands-free, her name, reading aloud, and which voice">
          <div className={s.embedded}>
            <VoiceSettings />
          </div>
        </Card>
        <Card
          title="Sky · access level"
          sub="What Sky may read, say and open for you"
        >
          <div className={s.panel}>
            <Access />
          </div>
        </Card>
        <Card
          title="Sky · what she has learned"
          sub="Questions asked, answers given, screens taught"
        >
          <div className={s.panel}>
            <Learning />
          </div>
        </Card>
        <Card title="Sky · beyond the platform" sub="a language model and a web search, if you connect them">
          <div className={s.embedded}>
            <Sources />
          </div>
        </Card>
        <Connection />
        <Card title="About" sub="this copy of the platform">
          <dl className={s.kv}>
            <dt>{t("Platform")}</dt>
            <dd>Sky Logics Technologies 1.0</dd>
            <dt>{t("Data")}</dt>
            <dd>{session.source.kind === "mock" ? t("built-in sample — nothing here is real") : t("the platform")}</dd>
            <dt>{t("Tenant")}</dt>
            <dd>{session.tenant ?? "—"}</dd>
            <dt>{t("Sites")}</dt>
            <dd>{session.sites.map((x) => x.code).join(", ")}</dd>
            <dt>{t("Signed in as")}</dt>
            <dd>{session.principal.name}</dd>
          </dl>
        </Card>
        {/* the same way out at the foot, for a long page — and for the first
            fifteen seconds, when Sky's greeting sits over the corner above */}
        <div className={s.row}>
          <Button variant="ghost" onClick={leave}>
            {t("Back")}
          </Button>
          <Button variant="primary" onClick={leave}>
            {t("Done")}
          </Button>
        </div>
      </div>
    </Page>
  );
}

/* ── appearance ──────────────────────────────────────────────────────── */

function Appearance() {
  const { theme, setTheme } = useTheme();
  const { lang, setLang } = useI18n();
  return (
    <Card title="Appearance" sub="kept in this browser">
      <div className={s.form}>
        <Field label="Theme">
          <Segmented<Theme>
            ariaLabel="Theme"
            value={theme}
            options={[
              { value: "dark", label: "Dark" },
              { value: "light", label: "Daylight" },
            ]}
            onChange={setTheme}
          />
        </Field>
        <Field label="Language">
          <Segmented<Lang>
            ariaLabel="Language"
            value={lang}
            options={[
              { value: "en", label: "English" },
              { value: "ar", label: "العربية" },
            ]}
            onChange={setLang}
          />
        </Field>
        <p className={s.note}>
          {t(
            "Arabic shows screens right to left. Numbers stay Western, dates Gregorian, and maps aren't mirrored. Some computed text may still be in English.",
          )}
        </p>
        <p className={s.note}>
          {t(
            "Daylight is a separate palette; status colors keep their meaning. The Daylight and Dark buttons in the header do the same thing.",
          )}
        </p>
      </div>
    </Card>
  );
}

/* ── the menu ────────────────────────────────────────────────────────── */

function Menu() {
  const nav = useNavLayout();
  const [customizing, setCustomizing] = useState(false);
  return (
    <Card
      title="Menu"
      sub={nav.store === "platform" ? "kept on the platform, under your sign-in" : "kept in this browser"}
    >
      <div className={s.form}>
        <p className={s.note}>
          {t(
            "Reorder, rename, create or hide suites and screens, and move screens between suites. New screens join their usual suite. Which suites are open is saved in this browser.",
          )}
        </p>
        <div className={s.row}>
          <Button onClick={() => setCustomizing(true)}>{t("Customize the menu")}</Button>
          <Button variant="ghost" onClick={() => void nav.reset()}>
            {t("Back to the default menu")}
          </Button>
        </div>
      </div>
      {customizing && <CustomizeMenuDialog onClose={() => setCustomizing(false)} />}
    </Card>
  );
}

/* ── the microphone ──────────────────────────────────────────────────── */

type MicState = "granted" | "denied" | "prompt" | "unknown";

/**
 * The permission, as the browser reports it. Chrome and Edge answer the
 * Permissions API for the microphone; Safari and Firefox do not, and say so by
 * throwing — which is "unknown" here, not "denied".
 */
function useMicrophonePermission(): [MicState, () => void] {
  const [state, setState] = useState<MicState>("unknown");
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    let status: PermissionStatus | null = null;
    const onChange = () => live && status && setState(status.state as MicState);
    navigator.permissions
      ?.query({ name: "microphone" as PermissionName })
      .then((st) => {
        if (!live) return;
        status = st;
        setState(st.state as MicState);
        st.addEventListener("change", onChange);
      })
      .catch(() => live && setState("unknown"));
    return () => {
      live = false;
      status?.removeEventListener("change", onChange);
    };
  }, [tick]);
  return [state, () => setTick((t) => t + 1)];
}

function Microphone() {
  const a = useAssistant();
  const [permission, recheck] = useMicrophonePermission();
  const [result, setResult] = useState<string | null>(null);
  const [heard, setHeard] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [voices, setVoices] = useState<voice.VoiceAvailability | null>(null);

  useEffect(() => {
    let live = true;
    voice.availability().then((v) => live && setVoices(v));
    return () => {
      live = false;
    };
  }, []);

  const embedded = typeof window !== "undefined" && window.self !== window.top;
  const secure = typeof window !== "undefined" && window.isSecureContext;
  const canHear = ears.isSupported();

  /*
   * Asking is the only way to get the browser's own prompt. A page cannot
   * grant itself a microphone; it can ask, and the browser asks the person.
   * The track is stopped the moment it is granted: this is a permission, not
   * a recording.
   */
  const allow = useCallback(async () => {
    setResult(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      for (const t of stream.getTracks()) t.stop();
      setResult(
        t("Allowed. Sky can listen — turn on hands-free or her name below, or press the microphone in her panel."),
      );
    } catch (e) {
      const name = e instanceof Error ? e.name : "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        setResult(
          embedded
            ? t(
                "Refused. This page is inside another page that blocks the microphone. Open the platform at its own HTTPS address and allow it there.",
              )
            : t(
                "Refused. Click the lock or the tuning icon at the left of the address bar, find Microphone, set it to Allow, then reload.",
              ),
        );
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        setResult(t("No microphone was found on this machine."));
      } else if (name === "SecurityError" || !secure) {
        setResult(
          t("The browser only opens a microphone on a secure page — HTTPS, or localhost. This page is not one."),
        );
      } else {
        setResult(t("Could not open the microphone: {reason}", { reason: e instanceof Error ? e.message : String(e) }));
      }
    } finally {
      recheck();
    }
  }, [embedded, recheck, secure]);

  const testListening = useCallback(() => {
    setHeard(null);
    setTesting(true);
    const ok = ears.listen(
      {
        onInterim: (words) => setHeard(words),
        onFinal: (words) => {
          setHeard(words ? t("Heard: “{text}”", { text: words }) : t("Nothing was heard."));
          setTesting(false);
        },
        onError: (m) => {
          setHeard(m);
          setTesting(false);
          recheck();
        },
      },
      voice.recognitionLang(a.prefs.voice.lang, a.prefs.voice.voiceURI),
    );
    if (!ok) setTesting(false);
  }, [a.prefs.voice.lang, a.prefs.voice.voiceURI, recheck]);

  const state: { cls: string; word: string } = !canHear
    ? { cls: s.bad, word: t("no speech recognition") }
    : permission === "granted"
      ? { cls: s.ok, word: t("allowed") }
      : permission === "denied"
        ? { cls: s.bad, word: t("refused") }
        : permission === "prompt"
          ? { cls: s.warn, word: t("not yet asked") }
          : { cls: s.warn, word: t("unknown until asked") };

  return (
    <Card title="Microphone and speech" sub="what Sky needs to hear you and to speak">
      <div className={s.form}>
        <dl className={s.kv}>
          <dt>{t("Microphone")}</dt>
          <dd>
            <span className={`${s.state} ${state.cls}`}>{state.word}</span>
          </dd>
          <dt>{t("Speech recognition")}</dt>
          <dd>
            {canHear
              ? t("available in this browser")
              : t("not in this browser — Chrome, Edge and Safari have it; Firefox does not")}
          </dd>
          <dt>{t("Voices")}</dt>
          <dd>
            {voices === null
              ? t("checking…")
              : voices.state === "ready"
                ? t("{n} installed on this machine", { n: voices.voices.length })
                : voices.state === "no-voices"
                  ? t("none installed — they come from the operating system")
                  : t("no speech synthesis in this browser")}
          </dd>
          <dt>{t("This page")}</dt>
          <dd>
            {embedded
              ? t(
                  "inside another page, which usually blocks the microphone",
                )
              : secure
                ? t("opened directly, on a secure address")
                : t("not a secure address — browsers only open a microphone over HTTPS or on localhost")}
          </dd>
        </dl>

        <div className={s.row}>
          <Button variant="primary" onClick={() => void allow()} disabled={!canHear && !navigator.mediaDevices}>
            {t("Allow the microphone")}
          </Button>
          <Button onClick={testListening} disabled={!canHear || testing}>
            {testing ? t("Listening…") : t("Test listening")}
          </Button>
          <Button
            variant="ghost"
            disabled={voices?.state !== "ready"}
            onClick={() => a.readAloud("Hello. I am Sky. If you can hear this, speech is working.")}
          >
            {t("Hear her voice")}
          </Button>
        </div>
        {result && <p className={s.note}>{result}</p>}
        {(testing || heard) && <div className={s.heard}>{heard ?? t("Say something…")}</div>}

        <p className={s.note}>
          {t(
            "Your browser holds this permission and remembers your choice; change it from the icon left of the address bar. In Chrome, audio is sent to Google for transcription.",
          )}
        </p>
      </div>
    </Card>
  );
}

/* ── the connection ──────────────────────────────────────────────────── */

function Connection() {
  const session = useSession();
  const [draft, setDraft] = useState<ConnectionOverride>(() => loadConnectionOverride());
  const overridden = Object.values(loadConnectionOverride()).some((v) => v !== undefined);

  const reload = () => {
    try {
      window.location.reload();
    } catch {
      /* a test runner without a real window: the setting is saved, the reload is the person's */
    }
  };

  return (
    <Card title="Connection" sub="Which platform this copy uses; applies on reload">
      <div className={s.form}>
        <dl className={s.kv}>
          <dt>{t("Now")}</dt>
          <dd>
            {session.source.kind === "mock" ? t("the built-in sample") : t("the platform")}
            {session.fallbackReason
              ? t(" — the platform could not be reached: {reason}", { reason: session.fallbackReason })
              : ""}
          </dd>
          <dt>{t("Platform address")}</dt>
          <dd>{config.apiUrl ?? t("none set")}</dd>
          <dt>{t("Default site")}</dt>
          <dd>{config.defaultSiteCode}</dd>
          {overridden && (
            <>
              <dt>{t("Overridden")}</dt>
              <dd>{t("yes — this browser's settings are on top of the build's")}</dd>
            </>
          )}
        </dl>

        <Field label="Data">
          <Select
            value={draft.dataSource ?? ""}
            onChange={(e) =>
              setDraft({ ...draft, dataSource: (e.target.value || undefined) as ConnectionOverride["dataSource"] })
            }
          >
            <option value="">{t("As built ({source})", { source: builtConfig.dataSource })}</option>
            <option value="auto">{t("Automatic — the platform when an address is set, else the sample")}</option>
            <option value="platform">{t("The platform only")}</option>
            <option value="mock">{t("The built-in sample only")}</option>
          </Select>
        </Field>
        <Field label="Platform address">
          <Input
            value={draft.apiUrl ?? ""}
            onChange={(e) => setDraft({ ...draft, apiUrl: e.target.value || undefined })}
            placeholder={builtConfig.apiUrl ?? t("https://platform.example/  or  /  when served by the platform")}
          />
        </Field>
        <Field label="API key">
          <Input
            type="password"
            value={draft.apiKey ?? ""}
            onChange={(e) => setDraft({ ...draft, apiKey: e.target.value || undefined })}
            placeholder={builtConfig.apiKey ? t("as built") : t("issued by the platform")}
          />
        </Field>
        <Field label="Default site code">
          <Input
            value={draft.defaultSiteCode ?? ""}
            onChange={(e) => setDraft({ ...draft, defaultSiteCode: e.target.value.toUpperCase() || undefined })}
            placeholder={builtConfig.defaultSiteCode}
          />
        </Field>
        <details>
          <summary className={s.note}>{t("Development headers")}</summary>
          <div className={s.form}>
            <Field label="Tenant">
              <Input
                value={draft.devTenant ?? ""}
                onChange={(e) => setDraft({ ...draft, devTenant: e.target.value || undefined })}
                placeholder={builtConfig.devTenant ?? t("only for a platform started with SLT_DEV_AUTH=1")}
              />
            </Field>
            <Field label="Principal">
              <Input
                value={draft.devPrincipal ?? ""}
                onChange={(e) => setDraft({ ...draft, devPrincipal: e.target.value || undefined })}
                placeholder={builtConfig.devPrincipal ?? ""}
              />
            </Field>
          </div>
        </details>

        <div className={s.row}>
          <Button
            variant="primary"
            onClick={() => {
              saveConnectionOverride(draft);
              reload();
            }}
          >
            {t("Apply and reload")}
          </Button>
          <Button
            variant="ghost"
            disabled={!overridden}
            onClick={() => {
              clearConnectionOverride();
              setDraft({});
              reload();
            }}
          >
            {t("Back to the build's settings")}
          </Button>
        </div>
        <p className={s.note}>
          {t(
            "Saved only in this browser, where anyone with access to its storage can read it. Use it for development or review copies, never real credentials. A copy served by the platform needs nothing here.",
          )}
        </p>
      </div>
    </Card>
  );
}
